import { Button } from "@rulebase/core/components";
import { useEffect, useState } from "react";
import { RiArrowUpLine } from "react-icons/ri";
import { PromptInput, PromptInputTextarea } from "./ui/prompt-input";

const SUBJECT_INTERVAL_MS = 5000;

/**
 * Steps through the labels on a timer. Paused while the field has text, since
 * the placeholder is hidden then and a moving element nobody can see is just a
 * render loop.
 */
function useCyclingIndex(count: number, enabled: boolean) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!enabled || count <= 1) return;
    const timer = setInterval(
      () => setIndex((current) => current + 1),
      SUBJECT_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [count, enabled]);

  // Modulo at read time so a label resolving late (or disappearing) can't leave
  // the index pointing past the end of the list.
  return count > 0 ? index % count : 0;
}

/**
 * A reel: the previous word is kept mounted and animated out while the new one
 * rolls in, so both are on screen together for the length of the transition.
 *
 * The two words are stacked in one grid cell rather than absolutely positioned,
 * so the box is always as wide as the wider of them. Sized to the incoming word
 * alone, a longer outgoing word would be sliced off by the clip on its way up.
 */
function RollingSubject(props: { text: string; className?: string }) {
  const [current, setCurrent] = useState(props.text);
  const [previous, setPrevious] = useState<string | null>(null);

  // Adjusting state during render, rather than in an effect: the swap is
  // derived from the prop, and an effect would paint the new word first and
  // start the roll a frame later.
  if (props.text !== current) {
    setPrevious(current);
    setCurrent(props.text);
  }

  return (
    <span className="inline-grid overflow-hidden leading-6">
      {previous !== null && (
        <span
          key={`out-${previous}`}
          className={`animate-prompt-subject-out col-start-1 row-start-1 ${props.className ?? ""}`}
          onAnimationEnd={() => setPrevious(null)}
        >
          {previous}
        </span>
      )}
      <span
        key={`in-${current}`}
        className={`animate-prompt-subject-in col-start-1 row-start-1 ${props.className ?? ""}`}
      >
        {current}
      </span>
    </span>
  );
}

/**
 * The prompt sits above the ticket, not below it: it is the first thing an
 * agent reaches for, and anchoring it to the bottom of a Zendesk panel buried
 * it under the content.
 *
 * The placeholder is rendered as an overlay rather than the textarea's own
 * `placeholder`, because the subject term cycles through what you can ask
 * about. A native placeholder can be neither animated nor styled per-word.
 */
export function TicketPromptInput(props: {
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: () => void;
  subjectLabels: string[];
}) {
  const isEmpty = props.value.trim().length === 0;
  const index = useCyclingIndex(props.subjectLabels.length, isEmpty);
  const subject = props.subjectLabels[index] ?? props.subjectLabels[0] ?? "";

  return (
    <PromptInput
      value={props.value}
      onValueChange={props.onValueChange}
      onSubmit={props.onSubmit}
      className="focus-within:border-primary-300 relative w-full rounded-xl border border-neutral-200 bg-white p-0 shadow-[0px_1px_2px_rgba(23,23,20,0.04),0px_4px_12px_rgba(23,23,20,0.06)] transition-colors"
    >
      <div className="flex items-center gap-2.5 px-3 py-2">
        {/* The bird mark, matching the composer on the app's home view. The
          * source is white-filled, so brightness-0 renders it as ink. */}
        <img
          src="/favicon.svg"
          alt=""
          aria-hidden
          className="size-4 shrink-0 brightness-0"
        />

        <div className="relative min-w-0 flex-1">
          {isEmpty && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-0 flex w-full items-center gap-1 overflow-hidden whitespace-nowrap text-neutral-500"
            >
              Search or ask anything about
              <RollingSubject
                text={subject}
                className="font-medium text-neutral-600"
              />
            </span>
          )}

          <PromptInputTextarea
            // The overlay stands in for it, but a real placeholder keeps the
            // field announced. Deliberately the stable first term, so a screen
            // reader isn't handed a name that changes under it.
            placeholder={`Search or ask anything about ${props.subjectLabels[0] ?? ""}`}
            disableAutosize
            autoFocus
            rows={1}
            className="min-h-0 resize-none p-0 leading-6 placeholder:text-transparent"
          />
        </div>

        <Button
          size="icon"
          square
          disabled={!props.value.trim()}
          onClick={props.onSubmit}
          className="size-6.5 shrink-0 rounded-[7px]"
        >
          <RiArrowUpLine className="size-3" />
        </Button>
      </div>
    </PromptInput>
  );
}
